/**
 * P-37 (D-194 Q-185 / Q-186) — the morning notice after an automatic clock-out
 * at 00:00. Pure, unit-tested (`src/utils/__tests__/autoCloseoutModel.test.ts`).
 */
import type { AutoCloseout } from '../../types';
import { israelWallToIso } from '../../utils/israelTime';

const pad = (n: number) => String(n).padStart(2, '0');

/** "HH:MM" of an instant, in the browser's (Israel) time; '' when missing. */
export function hhmm(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * T-65 — the automatic clock-out is stored at 23:59:59 (inside the day it
 * closes) and shown as "00:00". `source` says it when the record has one;
 * without it the exact 23:59:59 instant is the mark.
 */
export function isAutoMidnightInstant(iso: string | null | undefined, source?: string | null): boolean {
  if (source === 'auto_midnight') return true;
  if (source) return false;
  if (!iso) return false;
  const d = new Date(iso);
  return !Number.isNaN(d.getTime()) && d.getHours() === 23 && d.getMinutes() === 59 && d.getSeconds() >= 59;
}

/** "HH:MM" of a clock-out / break-end instant; "00:00" for the automatic one. */
export function clockOutHhmm(iso: string | null | undefined, source?: string | null): string {
  return isAutoMidnightInstant(iso, source) ? '00:00' : hhmm(iso);
}

/** The day the notice speaks of: "אתמול" when it is the day before `todayKey`, else its date. */
export function closeoutDayIsYesterday(workDate: string, todayKey: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(workDate) || !/^\d{4}-\d{2}-\d{2}$/.test(todayKey)) return false;
  const [y, m, d] = todayKey.split('-').map(Number);
  const prev = new Date(y, m - 1, d - 1);
  const key = `${prev.getFullYear()}-${pad(prev.getMonth() + 1)}-${pad(prev.getDate())}`;
  return key === workDate;
}

/** The instant of "HH:MM" on the day `date` (YYYY-MM-DD), or null when the time is not valid. */
export function isoOnDay(date: string, time: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{2}:\d{2}$/.test(time)) return null;
  return israelWallToIso(date, time); // FIX-TZ: Israeli time, not the browser's
}

/** The first day still waiting for an answer that was not put off ("אחר כך") in this session. */
export function nextToAsk(list: readonly AutoCloseout[], later: ReadonlySet<string>): AutoCloseout | null {
  return list.find((c) => c.status === 'pending' && !later.has(c.id)) ?? null;
}

/** The [לשנות] request: the clock-out time and each stopped timer's end, as typed. */
export function changeBody(c: AutoCloseout, clockOut: string, timerEnds: Record<string, string>) {
  return {
    action: 'change' as const,
    clock_out_at: isoOnDay(c.work_date, clockOut),
    timers: c.timers
      .filter((tm) => tm.work_log_id && timerEnds[tm.work_log_id])
      .map((tm) => ({ work_log_id: tm.work_log_id as string, end_time: isoOnDay(c.work_date, timerEnds[tm.work_log_id as string]) }))
      .filter((x): x is { work_log_id: string; end_time: string } => !!x.end_time),
  };
}

/** Session memory of "אחר כך" (it asks again at the next sign-in, and the attendance page keeps it). */
export const LATER_KEY = 'ote.autoCloseout.later';

export function readLater(storage: Pick<Storage, 'getItem'> | null): Set<string> {
  try {
    const raw = storage?.getItem(LATER_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(arr) ? arr.map(String) : []);
  } catch {
    return new Set();
  }
}

export function writeLater(storage: Pick<Storage, 'setItem'> | null, ids: Set<string>): void {
  try {
    storage?.setItem(LATER_KEY, JSON.stringify(Array.from(ids)));
  } catch {
    /* private mode: it simply asks again */
  }
}
